import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import zeroneLogoDark from 'assets/images/svg/logo-dark.svg'
import InputField from 'components/common/InputField'
import PrimaryButton from 'components/common/PrimaryButton'
import SecondaryButton from 'components/common/SecondaryButton'
import ZeroneLoader from 'components/common/ZeroneLoader'
import AnimatedBackgroundContainer from 'components/common/AnimatedBackgroundContainer'
import { useToast } from 'provider/ToastProvider'
import { forgotPasswordVerify, forgotPasswordReset, forgotPasswordSend } from 'constants/api/endpoints/AuthEndpoints'
import styles from 'styles/components/LoginForm.module.sass'

const ResetPasswordPage: React.FC = () => {
    const location   = useLocation()
    const email      = (location.state as { email?: string } | null)?.email ?? ''
    const navigate   = useNavigate()
    const { addToast } = useToast()

    const [step, setStep]               = useState<'otp' | 'password'>('otp')
    const [code, setCode]               = useState('')
    const [resetToken, setResetToken]   = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPw, setConfirmPw]     = useState('')
    const [loading, setLoading]         = useState(false)
    const [loadingMsg, setLoadingMsg]   = useState('')

    if (!email) {
        void navigate('/forgot-password')
        return null
    }

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault()
        if (code.trim().length !== 6) {
            addToast({ message: 'Please enter the 6-digit code.', type: 'error' })
            return
        }
        setLoading(true); setLoadingMsg('Verifying code...')
        const result = await forgotPasswordVerify({ tag: 'ResetPwVerify', data: { email, code: code.trim() } })
        setLoading(false)
        if (result.error ?? !result.resetToken) {
            addToast({ message: result.error ?? 'Verification failed.', type: 'error' })
            return
        }
        setResetToken(result.resetToken ?? '')
        setStep('password')
    }

    const handleResend = async () => {
        setLoading(true); setLoadingMsg('Resending code...')
        await forgotPasswordSend({ tag: 'ResendResetOtp', data: { email } })
        setLoading(false)
        addToast({ message: 'A new code has been sent.', type: 'success' })
    }

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault()
        if (newPassword !== confirmPw) {
            addToast({ message: 'Passwords do not match.', type: 'error' })
            return
        }
        setLoading(true); setLoadingMsg('Updating password...')
        const result = await forgotPasswordReset({ tag: 'ResetPw', data: { resetToken, newPassword, confirmPassword: confirmPw } })
        setLoading(false)
        if (result.error) {
            addToast({ message: result.error, type: 'error' })
            return
        }
        addToast({ message: 'Password updated! Please log in.', type: 'success' })
        void navigate('/login')
    }

    return (
        <AnimatedBackgroundContainer showFooter={true}>
            <form
                className={styles['login-form']}
                onSubmit={(e) => { void (step === 'otp' ? handleVerifyCode(e) : handleResetPassword(e)) }}
            >
                <div className={styles['logo-wrapper']}>
                    <Link to="/"><img src={zeroneLogoDark} className={styles['logo']} alt="Zerone AnalytiQs Logo" /></Link>
                </div>
                {loading ? (
                    <ZeroneLoader text={loadingMsg} />
                ) : step === 'otp' ? (
                    <div className={styles['main-wrapper']}>
                        <div className={styles['input-wrapper']}>
                            <p style={{ fontSize: 14, color: '#374151', margin: '0 0 8px', textAlign: 'center' }}>
                                Enter the 6-digit code sent to <strong>{email}</strong>.
                            </p>
                            <InputField label="Verification Code" type="text" value={code} onChange={setCode} required />
                        </div>
                        <div className={styles['action-buttons']}>
                            <PrimaryButton
                                text="Verify Code"
                                width="auto"
                                primaryColor="#FF5522"
                                textColor="#FFFFFF"
                                hoverColor="#FFFFFF"
                                hoverTextColor="#FF5522"
                                doSomething={(e: React.FormEvent) => { void handleVerifyCode(e) }}
                            />
                            <SecondaryButton
                                text="Resend code"
                                width="auto"
                                primaryColor="transparent"
                                textColor="#ff5522"
                                hoverColor="#ff5522"
                                hoverTextColor="#FFFFFF"
                                doSomething={() => { void handleResend() }}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={styles['main-wrapper']}>
                        <div className={styles['input-wrapper']}>
                            <InputField label="New Password" type="password" value={newPassword} onChange={setNewPassword} required />
                            <InputField label="Confirm Password" type="password" value={confirmPw} onChange={setConfirmPw} required />
                        </div>
                        <div className={styles['action-buttons']}>
                            <PrimaryButton
                                text="Update Password"
                                width="auto"
                                primaryColor="#FF5522"
                                textColor="#FFFFFF"
                                hoverColor="#FFFFFF"
                                hoverTextColor="#FF5522"
                                doSomething={(e: React.FormEvent) => { void handleResetPassword(e) }}
                            />
                        </div>
                    </div>
                )}
            </form>
        </AnimatedBackgroundContainer>
    )
}

export default ResetPasswordPage
